/**
 * Read-aloud in the browser (NADIA online, `tts: "browser"`): only voices that run on
 * the device (`localService`), so the answer never reaches a remote speech service
 * (Chrome's "online" voices send the text to Google).
 */

/** The local voice of `lang`: its Swiss variant first, then its Italian one, then any; null if none. */
export function pickLocalVoice(voices: SpeechSynthesisVoice[], lang: string): SpeechSynthesisVoice | null {
  const local = voices.filter(v => v.localService && v.lang.toLowerCase().startsWith(lang.toLowerCase()))
  const preferred = [`${lang}-CH`, `${lang}-IT`].map(l => l.toLowerCase())
  for (const tag of preferred) {
    const match = local.find(v => v.lang.toLowerCase().replace('_', '-') === tag)
    if (match) return match
  }
  return local[0] ?? null
}

/** The text to speak: the server's clean_text_for_tts rules, plus link URLs and the space before [N]. */
export function cleanTextForSpeech(text: string): string {
  return text
    .replace(/\n+\*{0,2}Fonti:?\*{0,2}\n[\s\S]*$/i, '')
    .replace(/\s*\[\d+\]/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/(^|\W)_(.+?)_(?=\W|$)/g, '$1$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/^(---+|\*\*\*+)$/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Speak `text` with `voice`; `onEnd` runs when it ends or is stopped. Returns the stop function. */
export function speakWithBrowser(text: string, voice: SpeechSynthesisVoice, onEnd: () => void): () => void {
  const synth = window.speechSynthesis
  const utterance = new SpeechSynthesisUtterance(cleanTextForSpeech(text))
  utterance.voice = voice
  utterance.lang = voice.lang
  let ended = false
  const finish = () => {
    if (!ended) {
      ended = true
      onEnd()
    }
  }
  utterance.onend = finish
  utterance.onerror = finish
  synth.cancel()
  synth.speak(utterance)
  return () => {
    synth.cancel()
    finish()
  }
}
